import React from 'react';
import {useTranslation} from 'react-i18next';
import './ShopifyMerchant.scss';

const SHOPIFY_APP_STORE_URL = 'https://apps.shopify.com';

// El camino de la app personalizada (link de instalación, tanda 23/10): el backend vuelve con
// `?reason=<código>&flow=custom_app` (`contrato.md` §5).
export const FLOW_CUSTOM_APP = 'custom_app';

const REASON_TO_MESSAGE = {
    INVALID_CODE: 'El código no es válido. Verificá con tu agencia o pedile uno nuevo.',
    INVALID_HMAC: 'La sesión de Shopify no se pudo validar. Volvé al Shopify App Store y reinstalá la app.',
    INVALID_SHOP_FORMAT: 'El subdominio de la tienda es inválido. Volvé al Shopify App Store y reinstalá la app.',
    INVALID_REQUEST: 'Faltan datos para completar la instalación. Volvé al Shopify App Store y reinstalá la app.',
    OAUTH_FAILED: 'No pudimos completar la autorización con Shopify. Volvé al Shopify App Store y reinstalá la app.',
    OAUTH_EXPIRED: 'La sesión de Shopify caducó. Volvé al Shopify App Store y reinstalá la app.',
    STATE_MISMATCH: 'Error de seguridad al conectar con Shopify. Reintentá desde tu cuenta de Dashbo.',
    REDEEM_FAILED: 'No pudimos conectar tu tienda. Intentá nuevamente. Si el problema persiste contactá a soporte: support@dashbo.io.',
    NETWORK: 'Hubo un problema de conexión. Verificá tu internet e intentá de nuevo.',
};

/**
 * La vuelta de Shopify cuando la instalación falló. Con `flow=custom_app` (la app personalizada del link de
 * instalación) hay un solo texto para todos los códigos, por i18n (`copy.md` §4): volver a abrir el link o
 * pedir uno nuevo a la agencia. Ahí no se ofrece «Volver al Shopify App Store»: esa app no se instala desde la
 * tienda de apps. El camino de la app pública queda como estaba (sus textos todavía no pasan por i18n).
 */
export default function ShopifyMerchantError() {
    const {t} = useTranslation();
    const params = new URLSearchParams(window.location.search);

    if (params.get('flow') === FLOW_CUSTOM_APP) {
        return (
            <ErrorCard title={t('shopify_link.install_error_title')} message={t('shopify_link.install_error_body')}
                       showAppStoreLink={false}/>
        );
    }

    const reason = params.get('reason') || 'REDEEM_FAILED';
    const customMessage = params.get('message') || '';
    const message = REASON_TO_MESSAGE[reason] || customMessage || REASON_TO_MESSAGE.REDEEM_FAILED;

    return <ErrorCard title="No se pudo conectar la tienda" message={message} showAppStoreLink/>;
}

function ErrorCard({title, message, showAppStoreLink}) {
    return (
        <div className="merchant-install-page">
            <div className="merchant-install-card">
                <img
                    src="/logo-light.svg"
                    alt="Dashbo"
                    className="merchant-install-logo"
                    onError={(e) => {
                        e.target.style.display = 'none';
                    }}
                />
                <div className="merchant-install-error" aria-hidden="true">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                        <circle cx="32" cy="32" r="30" fill="#DF0D00"/>
                        <path d="M22 22L42 42M42 22L22 42" stroke="#fff" strokeWidth="4" strokeLinecap="round"/>
                    </svg>
                </div>
                <h1>{title}</h1>
                <p>{message}</p>
                {showAppStoreLink && (
                    <a href={SHOPIFY_APP_STORE_URL} className="merchant-install-link" rel="noreferrer">
                        Volver al Shopify App Store
                    </a>
                )}
            </div>
        </div>
    );
}
